import {ReactNode, createContext, useContext, useEffect, useMemo, useState, useCallback} from "react";
import {User} from "../models";
import {getInfos} from "../services/company";
import {COOKIES_COMPANY_ID} from "../helper/constants";
// the cookies module only: the contexts don't need the token verifier
import {cookies} from "../shared/session/cookies";
import {sessionConfig} from "../session";


interface AuthState {
    user: User | null;
    status: "loading" | "authenticated" | "unauthenticated";
}
interface AuthContextValue extends AuthState {
    fetchUser: () => Promise<void>
    refreshUser: () => Promise<void>
    logout: () => void
    pendingIdentity: boolean
    verified: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider(props: {children: ReactNode}) {
    const [user, setUser] = useState<User | null>(null);
    const [status, setStatus] = useState<AuthState["status"]>("loading");
    const pendingIdentity = useMemo(function () {
        return user?.verificationStatus === "pending";
    }, [user]);
    const verified = useMemo(function () {
        return user?.verificationStatus === "verified";
    }, [user]);
    const fetchUser = useCallback(async function () {
        let res: User;
        try {
            setStatus("loading");
            res = await getInfos();
            if (res) {
                setUser(res);
                cookies.set(COOKIES_COMPANY_ID, `${res.CompanyId}`, sessionConfig);
                setStatus("authenticated");
            } else {
                setStatus("unauthenticated");
            }
        } catch (err) {
            console.error("Error fetch user infos: ", err);
            setUser(null);
            setStatus("unauthenticated");
        }
    }, []);
    // Re-read the user without leaving the page (no loading state): the
    // identity verification happens in the account app, in another tab or
    // before coming back here.
    const refreshUser = useCallback(async function () {
        let res: User;
        try {
            res = await getInfos();
            if (res) {
                setUser(res);
            }
        } catch (err) {
            console.error("Error refreshing user infos: ", err);
        }
    }, []);
    useEffect(function () {
        function onVisible() {
            if (document.visibilityState === "visible" && status === "authenticated") {
                refreshUser();
            }
        }
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [refreshUser, status]);
    const logout = useCallback(function () {
        setUser(null);
        setStatus("unauthenticated");
    }, []);
    const value = useMemo(() => ({
        fetchUser,
        logout,
        pendingIdentity,
        refreshUser,
        status,
        user,
        verified
    }), [fetchUser, logout, pendingIdentity, refreshUser, status, user, verified]);
    return <AuthContext.Provider value={value}>
        {props.children}
    </AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth must be used within AuthProvider");
    }
    return ctx;
}
